'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { Close, Plus } from '../../../icons';
import Button from '../../Button';
import CurrencyField from '../CurrencyField';
import TextField from '../TextField';
import { nanoid } from 'nanoid';
import styles from './multiCurrencyField.module.scss';

export type MultiCurrencyOption = {
  id: string;
  label: string;
  value?: number;
};

type MultiCurrencyFieldProps = {
  options: MultiCurrencyOption[];
  setOptions: (options: MultiCurrencyOption[]) => void;
  optionLabelPlaceholder?: string;
  addOptionLabel?: string;
  summationLabel?: string;
};

export default function MultiCurrencyField({
  options,
  optionLabelPlaceholder,
  addOptionLabel,
  summationLabel,
  setOptions
}: MultiCurrencyFieldProps) {
  return (
    <div>
      <AnimatePresence initial={false}>
        {options.map((option, index) => (
          <motion.div
            key={option.id}
            className={styles.optionRow}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <TextField
              type="text"
              placeholder={optionLabelPlaceholder || `Fee ${index + 1}`}
              className={styles.optionLabel}
              value={option.label}
              onChange={(e) =>
                setOptions(
                  options.map((opt) =>
                    opt.id === option.id
                      ? { ...opt, label: e.target.value }
                      : opt
                  )
                )
              }
            />
            <CurrencyField
              className={styles.optionValue}
              value={option.value}
              onValueChange={(value) => {
                setOptions(
                  options.map((opt) =>
                    opt.id === option.id
                      ? { ...opt, value: value.floatValue }
                      : opt
                  )
                );
              }}
            />
            {options.length === 1 ? (
              <div className={styles.deleteSpacer} />
            ) : (
              <Button
                type="button"
                size="iconSmall"
                colorScheme="tertiary"
                className={styles.deleteButton}
                onClick={() =>
                  setOptions(options.filter((opt) => opt.id !== option.id))
                }
              >
                <Close />
              </Button>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
      <Button
        type="button"
        size="small"
        colorScheme="tertiary"
        className={styles.addButton}
        onClick={() =>
          setOptions([
            ...options,
            { id: nanoid(), label: '', value: undefined }
          ])
        }
      >
        <Plus />
        <span>{addOptionLabel || 'Add Option'}</span>
      </Button>
      {summationLabel && (
        <div className={styles.summation}>
          <span>{summationLabel}</span>
          <span>
            {options
              .reduce((sum, option) => sum + (option.value || 0), 0)
              .toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD'
              })}
          </span>
        </div>
      )}
    </div>
  );
}
